import { useState } from "react";
import { Plus, Trash2, Save, X, ChevronDown } from "lucide-react";
import type { BillingProcess, AdminConfig } from "../types";
import { getDefaultAdminConfig } from "../lib/api";

export function AdminView({
  config,
  onSave,
  onClose,
}: {
  config: AdminConfig;
  onSave: (config: AdminConfig) => void;
  onClose: () => void;
}) {
  const [processes, setProcesses] = useState<BillingProcess[]>([...config.processes]);
  const [billing, setBilling] = useState<AdminConfig["billing"]>({ ...config.billing });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<BillingProcess>({
    processId: 0,
    title: "",
    pickupEvent: "Created",
    isActive: true,
  });

  const handleAddProcess = () => {
    const newProcess: BillingProcess = {
      processId: 0,
      title: "",
      pickupEvent: "Created",
      isActive: true,
    };
    setProcesses((prev: BillingProcess[]) => [...prev, newProcess]);
    setEditingId(0); // temporary ID for new process
    setEditForm(newProcess);
  };

  const handleEditProcess = (process: BillingProcess) => {
    setEditingId(process.processId);
    setEditForm({ ...process });
  };

  const handleSaveEdit = () => {
    setProcesses((prev: BillingProcess[]) =>
      prev.map((p) => (p.processId === editingId ? { ...editForm } : p))
    );
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    if (editForm.processId === 0) {
      // Remove the newly added process
      setProcesses((prev: BillingProcess[]) => prev.slice(0, -1));
    }
    setEditingId(null);
  };

  const handleDeleteProcess = (processId: number) => {
    setProcesses((prev: BillingProcess[]) => prev.filter((p) => p.processId !== processId));
  };

  const handleBillingChange = (field: keyof AdminConfig["billing"], value: number) => {
    setBilling((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAll = () => {
    onSave({ processes, billing });
    onClose();
  };

  return (
    <div className="admin-view">
      <header className="admin-header">
        <h1>Administration - Configuration Facturation SPT</h1>
        <div className="admin-header-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            <X size={18} /> Fermer
          </button>
          <button className="btn btn-primary" onClick={handleSaveAll}>
            <Save size={18} /> Enregistrer tout
          </button>
        </div>
      </header>

      <section className="admin-section">
        <h2>Paramètres de facturation</h2>
        <div className="billing-config-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fixed-base">Forfait fixe mensuel (FCFA)</label>
              <input
                id="fixed-base"
                type="number"
                value={billing.fixedBaseFcfa}
                onChange={(e) => handleBillingChange("fixedBaseFcfa", Number(e.target.value) || 0)}
                min="0"
                step="1000"
              />
            </div>
            <div className="form-group">
              <label htmlFor="price-per-pickup">Prix par collecte (FCFA)</label>
              <input
                id="price-per-pickup"
                type="number"
                value={billing.pricePerPickupFcfa}
                onChange={(e) => handleBillingChange("pricePerPickupFcfa", Number(e.target.value) || 0)}
                min="0"
                step="1"
              />
            </div>
          </div>
          <p className="config-hint">
            Modèle recommandé : <strong>300 000 FCFA + 25 FCFA/collecte</strong>
            (basé sur les simulations de mai 2026)
          </p>
        </div>
      </section>

      <section className="admin-section">
        <div className="section-header">
          <h2>Processus inclus dans la facturation</h2>
          <button className="btn btn-secondary" onClick={handleAddProcess}>
            <Plus size={18} /> Ajouter un processus
          </button>
        </div>

        {processes.length === 0 && (
          <p className="no-processes">Aucun processus configuré. Cliquez sur "Ajouter un processus" pour commencer.</p>
        )}

        <div className="processes-table-wrapper">
          <table className="processes-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom du processus</th>
                <th>Événement de collecte</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {processes.map((process, index) => {
                const isEditing = editingId === process.processId || (editingId === 0 && index === processes.length - 1);
                const isNew = process.processId === 0;

                if (isEditing) {
                  return (
                    <tr key={index} className="editing-row">
                      <td>
                        <input
                          type="number"
                          value={editForm.processId || ""}
                          onChange={(e) => setEditForm({ ...editForm, processId: Number(e.target.value) || 0 })}
                          placeholder="ID"
                          disabled={!isNew}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          placeholder="Nom du processus"
                        />
                      </td>
                      <td>
                        <select
                          value={editForm.pickupEvent}
                          onChange={(e) => setEditForm({ ...editForm, pickupEvent: e.target.value as "Created" | "Collecté" })}
                        >
                          <option value="Created">Création (Created)</option>
                          <option value="Collecté">Collecte (Collecté)</option>
                        </select>
                      </td>
                      <td>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editForm.isActive}
                            onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                          />
                          <span>Oui</span>
                        </label>
                      </td>
                      <td className="actions-cell">
                        <button className="icon-btn save" onClick={handleSaveEdit} title="Enregistrer">
                          <Save size={16} />
                        </button>
                        <button className="icon-btn cancel" onClick={handleCancelEdit} title="Annuler">
                          <X size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={process.processId || index}>
                    <td>{process.processId || <span className="new-badge">Nouveau</span>}</td>
                    <td className="process-title">{process.title || <em>Sans nom</em>}</td>
                    <td>
                      <span className={`pickup-badge ${process.pickupEvent.toLowerCase()}`}>
                        {process.pickupEvent}
                      </span>
                    </td>
                    <td>
                      <span className={process.isActive ? "status-active" : "status-inactive"}>
                        {process.isActive ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button className="icon-btn edit" onClick={() => handleEditProcess(process)} title="Modifier">
                        <ChevronDown size={16} />
                      </button>
                      <button className="icon-btn delete" onClick={() => handleDeleteProcess(process.processId)} title="Supprimer">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="processes-help">
          <h4>Note importante :</h4>
          <ul>
            <li>Par défaut, une collecte compte à la <strong>création du dossier</strong> (étape "Created")</li>
            <li>Si le processus a une étape exactement nommée <strong>"Collecté"</strong>, la collecte compte au premier passage à cette étape</li>
            <li>Processus exclus : 168 (Dépêches inter-agences - interne), 56, 68, 78, 79, 89 (prototypes / faible volume)</li>
          </ul>
        </div>
      </section>

      <section className="admin-section reset-section">
        <button className="btn btn-danger" onClick={() => onSave(getDefaultAdminConfig())}>
          Réinitialiser à la configuration par défaut
        </button>
      </section>
    </div>
  );
}